// ============================================
// PII Detection & Tokenization Engine
// ============================================
// Ported from Aditya Lens desktop app (185 tests, 0 leaks).
// 18 hardened regex patterns with severity levels,
// context-aware detection, and industry-specific
// compliance modes (Medical, Dental, Legal, Financial).
//
// This module is a STANDALONE scanner — no database
// or network calls. It works in both Node.js server
// functions and browser contexts.
// ============================================

// ── Types ────────────────────────────────────────────────────────────────────

export interface PIIPattern {
  key: string;
  label: string;
  regex: RegExp;
  tag: string;
  severity: "critical" | "high" | "medium" | "low";
  contextRequired?: boolean;
}

export interface PIIMatch {
  start: number;
  end: number;
  original: string;
  tag: string;
  key: string;
  severity: "critical" | "high" | "medium" | "low";
  masked: string;
  token: string; // replacement token like [PII_EMAIL_1]
}

export interface RedactionResult {
  redactedText: string;
  tokenMap: Record<string, string>; // { "[PII_EMAIL_1]": "john@example.com" }
  findings: number;
  categoryCounts: Record<string, number>;
  severityCounts: Record<string, number>;
  matches: PIIMatch[];
}

export type ComplianceMode = "general" | "medical" | "dental" | "legal" | "financial";

// ── 18 Hardened PII Patterns ─────────────────────────────────────────────────

const PII_PATTERNS: Record<string, PIIPattern> = {
  email: {
    key: "email",
    label: "Email Addresses",
    regex: /\b[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}\b/g,
    tag: "EMAIL",
    severity: "high",
  },
  phone: {
    key: "phone",
    label: "Phone Numbers",
    regex: /(?:\+?\d{1,3}[-.\s]?)?(?:\(?\d{2,5}\)?[-.\s]?)\d{3,4}[-.\s]?\d{3,4}\b/g,
    tag: "PHONE",
    severity: "high",
  },
  nhs: {
    key: "nhs",
    label: "NHS Numbers",
    regex: /\b\d{3}[\s\-]?\d{3}[\s\-]?\d{4}\b/g,
    tag: "NHS",
    severity: "critical",
  },
  ni_number: {
    key: "ni_number",
    label: "National Insurance Numbers",
    regex: /\b(?!BG|GB|KN|NK|NT|TN|ZZ)[A-CEGHJ-PR-TW-Z][A-CEGHJ-NPR-TW-Z]\s?\d{2}\s?\d{2}\s?\d{2}\s?[A-D]\b/gi,
    tag: "NI",
    severity: "critical",
  },
  credit_card: {
    key: "credit_card",
    label: "Credit Card Numbers",
    regex: /\b(?:4\d{3}|5[1-5]\d{2}|3[47]\d{2}|6(?:011|5\d{2}))[-\s]?\d{4}[-\s]?\d{4}[-\s]?\d{4}\b/g,
    tag: "CARD",
    severity: "critical",
  },
  ssn: {
    key: "ssn",
    label: "Social Security Numbers",
    regex: /\b(?!000|666|9\d{2})\d{3}[-\s]\d{2}[-\s]\d{4}\b/g,
    tag: "SSN",
    severity: "critical",
  },
  uk_postcode: {
    key: "uk_postcode",
    label: "UK Postcodes",
    regex: /\b[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}\b/gi,
    tag: "POSTCODE",
    severity: "medium",
  },
  dob: {
    key: "dob",
    label: "Dates of Birth",
    regex: /\b(?:\d{1,2}[\/\-.]\d{1,2}[\/\-.]\d{2,4}|\d{4}[\/\-.]\d{1,2}[\/\-.]\d{1,2})\b/g,
    tag: "DOB",
    severity: "high",
  },
  sort_code: {
    key: "sort_code",
    label: "Sort Codes",
    regex: /\b\d{2}[-\s]\d{2}[-\s]\d{2}\b/g,
    tag: "SORT",
    severity: "high",
  },
  iban: {
    key: "iban",
    label: "IBAN Numbers",
    regex: /\b[A-Z]{2}\d{2}\s?[A-Z0-9]{4}(?:\s?[A-Z0-9]{4}){2,7}(?:\s?[A-Z0-9]{1,4})?\b/g,
    tag: "IBAN",
    severity: "critical",
  },
  vat: {
    key: "vat",
    label: "VAT Numbers",
    regex: /\b(?:GB\s?\d{3}\s?\d{4}\s?\d{2,3}|(?:DE|FR|IT|ES|NL|BE|AT|PL|SE|DK|FI|IE|PT|CZ|RO|HU|SK|BG|HR|SI|LT|LV|EE|CY|LU|MT)\s?[A-Z0-9]{8,12})\b/g,
    tag: "VAT",
    severity: "high",
  },
  ip_address: {
    key: "ip_address",
    label: "IP Addresses",
    regex: /\b(?:(?:25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(?:25[0-5]|2[0-4]\d|[01]?\d\d?)\b/g,
    tag: "IP",
    severity: "medium",
  },
  driving_license: {
    key: "driving_license",
    label: "Driving Licence Numbers",
    regex: /\b[A-Z]{2,5}\d{6}[A-Z\d]{2,6}\b/g,
    tag: "DL",
    severity: "critical",
  },
  person_name: {
    key: "person_name",
    label: "Person Names (with titles)",
    regex: /\b(?:Mr|Mrs|Ms|Miss|Dr|Prof|Rev|Sir|Dame|Cllr|Lord|Lady|Mx)\.?\s+[A-Z][a-z]+(?:\s+[A-Z][a-z]+){0,3}\b/g,
    tag: "NAME",
    severity: "high",
  },
  passport: {
    key: "passport",
    label: "Passport Numbers",
    regex: /\b(?:\d{9}|[A-Z]{1,2}\d{7,9})\b/g,
    tag: "PASSPORT",
    severity: "critical",
  },
  bank_account: {
    key: "bank_account",
    label: "Bank Account Numbers",
    regex: /\b\d{8}\b/g,
    tag: "BANK_ACCT",
    severity: "high",
    contextRequired: true,
  },
  mac_address: {
    key: "mac_address",
    label: "MAC Addresses",
    regex: /\b([0-9A-Fa-f]{2}[:\-]){5}[0-9A-Fa-f]{2}\b/g,
    tag: "MAC",
    severity: "low",
  },
  vehicle_reg: {
    key: "vehicle_reg",
    label: "UK Vehicle Registration",
    regex: /\b[A-Z]{2}\d{2}\s?[A-Z]{3}\b/g,
    tag: "VREG",
    severity: "medium",
  },
  swift_bic: {
    key: "swift_bic",
    label: "SWIFT/BIC Codes",
    regex: /\b[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}(?:[A-Z0-9]{3})?\b/g,
    tag: "SWIFT",
    severity: "high",
    contextRequired: true,
  },
  medical_id: {
    key: "medical_id",
    label: "Medical Record Numbers",
    regex: /\b(?:MRN|MED|PAT|PATIENT)[:\-#\s]\s?\d{5,10}\b/gi,
    tag: "MEDICAL",
    severity: "critical",
  },
};

// Banking context words — only flag bank_account and swift_bic when near these
const BANK_CONTEXT =
  /(?:account|acct|a\/c|bank|sort|routing|swift|bic|iban|transfer|payment|balance)/i;

// ── Industry Compliance Modes ────────────────────────────────────────────────

export const COMPLIANCE_MODES: Record<ComplianceMode, string[]> = {
  general: Object.keys(PII_PATTERNS), // all patterns
  medical: [
    "email", "phone", "nhs", "ni_number", "ssn", "dob", "person_name",
    "medical_id", "ip_address", "uk_postcode",
  ],
  dental: [
    "email", "phone", "nhs", "dob", "person_name", "medical_id",
    "credit_card", "uk_postcode",
  ],
  legal: [
    "email", "phone", "ni_number", "ssn", "person_name", "dob",
    "bank_account", "sort_code", "iban", "credit_card", "passport",
    "driving_license", "uk_postcode",
  ],
  financial: [
    "email", "phone", "ssn", "ni_number", "credit_card", "bank_account",
    "sort_code", "iban", "swift_bic", "vat", "person_name",
  ],
};

// ── Exported pattern info (for UI display) ───────────────────────────────────

export function getPatternInfo(): Array<{ key: string; label: string; severity: string }> {
  return Object.values(PII_PATTERNS).map((p) => ({
    key: p.key,
    label: p.label,
    severity: p.severity,
  }));
}

export function getActivePatterns(mode: ComplianceMode): Array<{ key: string; label: string; severity: string }> {
  const activeKeys = COMPLIANCE_MODES[mode] || COMPLIANCE_MODES.general;
  return activeKeys
    .map((k) => PII_PATTERNS[k])
    .filter(Boolean)
    .map((p) => ({ key: p.key, label: p.label, severity: p.severity }));
}

// ── Core Scan Function ──────────────────────────────────────────────────────

export function scanText(text: string, enabledFilters: string[]): PIIMatch[] {
  const matches: PIIMatch[] = [];
  const counters: Record<string, number> = {};

  for (const key of enabledFilters) {
    const pat = PII_PATTERNS[key];
    if (!pat) continue;

    // Create a fresh regex instance to avoid shared lastIndex state
    const re = new RegExp(pat.regex.source, pat.regex.flags);
    let m: RegExpExecArray | null;

    while ((m = re.exec(text)) !== null) {
      // Context check for bank_account, swift_bic
      if (pat.contextRequired) {
        const ctxStart = Math.max(0, m.index - 80);
        const ctxEnd = Math.min(text.length, m.index + m[0].length + 80);
        const surrounding = text.substring(ctxStart, ctxEnd);
        if (!BANK_CONTEXT.test(surrounding)) continue;
      }

      counters[key] = (counters[key] || 0) + 1;
      const token = `[PII_${pat.tag}_${counters[key]}]`;

      matches.push({
        start: m.index,
        end: m.index + m[0].length,
        original: m[0],
        tag: pat.tag,
        key,
        severity: pat.severity,
        masked:
          m[0].substring(0, 2) +
          "*".repeat(Math.max(m[0].length - 4, 0)) +
          m[0].substring(m[0].length - 2),
        token,
      });

      // Safety limit
      if (matches.length > 5000) break;
    }
  }

  // Sort by position (descending) for safe replacement
  matches.sort((a, b) => b.start - a.start);

  // Deduplicate overlapping matches
  const used: { start: number; end: number }[] = [];
  return matches.filter((m) => {
    if (used.some((u) => m.start < u.end && m.end > u.start)) return false;
    used.push({ start: m.start, end: m.end });
    return true;
  });
}

// ── Redact and Tokenize ─────────────────────────────────────────────────────

export function redactText(text: string, enabledFilters: string[]): RedactionResult {
  const matches = scanText(text, enabledFilters);

  let redactedText = text;
  const tokenMap: Record<string, string> = {};
  const categoryCounts: Record<string, number> = {};
  const severityCounts: Record<string, number> = {
    critical: 0,
    high: 0,
    medium: 0,
    low: 0,
  };

  // Matches are already sorted descending by position — safe to replace in order
  for (const m of matches) {
    redactedText =
      redactedText.substring(0, m.start) +
      m.token +
      redactedText.substring(m.end);
    tokenMap[m.token] = m.original;
    categoryCounts[m.key] = (categoryCounts[m.key] || 0) + 1;
    severityCounts[m.severity] = (severityCounts[m.severity] || 0) + 1;
  }

  return {
    redactedText,
    tokenMap,
    findings: matches.length,
    categoryCounts,
    severityCounts,
    matches,
  };
}

// ── Rehydrate (swap tokens back to originals for authorized viewers) ────────

export function rehydrateText(
  redactedText: string,
  tokenMap: Record<string, string>
): string {
  let result = redactedText;
  for (const [token, original] of Object.entries(tokenMap)) {
    result = result.replace(token, original);
  }
  return result;
}

// ── Quick check: does this text contain PII? ────────────────────────────────

export function hasPII(
  text: string,
  mode: ComplianceMode = "general"
): boolean {
  const filters = COMPLIANCE_MODES[mode] || COMPLIANCE_MODES.general;
  return scanText(text, filters).length > 0;
}
